import React, { useCallback, useEffect, useState } from "react";
import { InputHelperText, useDataProvider, useInput, useTranslate } from "react-admin";
import { useWatch, useFormContext } from "react-hook-form";

import { Checkbox, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";

import LinearProgress from "./LinerProgress";
import { FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS } from "../utils/parameters";

import { Brand, FetchedListData, Prefecture } from "../infrastructure/types";

const commonStyles = {
  container: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "18px",
  },
  form: {
    width: 256,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
  },
  chip: {
    margin: 2,
  },
  checkbox: {
    height: "52px",
    margin: "8px 0 4px",
  },
};

export const BrandSelectInput = (props: any) => {
  const dataProvider = useDataProvider();
  const translate = useTranslate();

  const { field, isRequired, fieldState } = useInput(props);
  const { onBlur, onChange, value } = field;
  const { error } = fieldState;
  const { setValue } = useFormContext();

  const [isLoading, setIsLoading] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [prefectures, setPrefectures] = useState<Prefecture[]>([]);
  const [isFiltered, setIsFiltered] = useState(true);
  const _prefectureIds: number[] = useWatch({ name: "prefecture_ids" });

  useEffect(() => {
    let isCleanedUp = false;

    (async () => {
      if (!isCleanedUp) {
        setIsLoading(true);
      }

      const params = {
        ...FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS,
      };
      const fetchedBrands: FetchedListData<Brand> = await dataProvider.getList("brands", params);
      const fetchedPrefectures: FetchedListData<Prefecture> = await dataProvider.getList("prefectures", params);

      if (!isCleanedUp) {
        if (fetchedBrands && fetchedPrefectures) {
          const initialBrandId = value;
          onChange(initialBrandId);
          setBrands(fetchedBrands.data);
          setPrefectures(fetchedPrefectures.data);

          const prefectureIds = _prefectureIds
            ? fetchedPrefectures.data.filter((p) => _prefectureIds.includes(p.id)).map((p) => p.id)
            : [];

          // 新規登録時は絞り込みありとし、編集 or 複製時はその銘柄に県産が登録されているかで絞り込みを判断
          setIsFiltered(() => {
            if (!prefectureIds.length) {
              // 県産未選択
              return !initialBrandId;
            }
            const filteredBrands = fetchedBrands.data.filter((brand) =>
              prefectureIds.every((pid) => brand.prefecture_ids.includes(pid))
            );
            return !initialBrandId || filteredBrands.some((brand) => brand.id === initialBrandId);
          });
        }
        setIsLoading(false);
      }
    })();

    return () => {
      isCleanedUp = true;
    };
  }, [dataProvider, value]);

  useEffect(() => {
    if (isFiltered && _prefectureIds) {
      const prefectureIds = prefectures.filter((p) => _prefectureIds.includes(p.id)).map((p) => p.id);

      if (prefectureIds.length > 0) {
        const filteredBrands = brands.filter((brand) =>
          prefectureIds.every((pid) => brand.prefecture_ids.includes(pid))
        );
        const brandIds = filteredBrands.map((b) => b.id);

        if (value && !brandIds.includes(value)) {
          setValue(props.source, "", { shouldValidate: true, shouldTouch: true });
        }
      }
    }
  }, [isFiltered, _prefectureIds, prefectures, brands, value, onChange, setValue, props.source]);

  const handleChangeCheckbox = useCallback(() => setIsFiltered(!isFiltered), [isFiltered, setIsFiltered]);
  const handleChangeSelect = (ev: SelectChangeEvent<string | number>) => onChange(ev.target.value);

  const label = translate(`resources.offers.fields.brand_id`);
  const requiredLabel = isRequired ? " *" : "";

  if (isLoading) {
    return (
      <div>
        <InputLabel shrink={true}>{`${label}${requiredLabel}`}</InputLabel>
        <LinearProgress />
      </div>
    );
  }

  let items = [...brands];

  if (isFiltered && _prefectureIds) {
    const prefectureIds = prefectures.filter((p) => _prefectureIds.includes(p.id)).map((p) => p.id);

    // 県産が選択されている場合、選択している県産が全て含まれている銘柄のみを抽出する
    // 県産が未選択の場合はフィルタリングをしない
    if (prefectureIds.length !== 0) {
      items = items.filter((brand) => prefectureIds.every((pid) => brand.prefecture_ids.includes(pid)));
    }
  }

  const sortedItems = items.concat([]).sort((a, b) => {
    // 銘柄不問は一番最後に来るようにする
    if (a.name.includes("銘柄不問")) {
      return 1;
    }
    if (b.name.includes("銘柄不問")) {
      return -1;
    }

    // それ以外はふりがなであいうえお順
    return a.name_kana.localeCompare(b.name_kana, "ja");
  });

  return (
    <div style={commonStyles.container}>
      <FormControl variant="filled" sx={commonStyles.form} margin="dense" error={!!error}>
        <InputLabel id="label">{`${label}${requiredLabel}`}</InputLabel>
        <Select
          labelId="label"
          id="select"
          value={value}
          onBlur={(event) => onBlur(event)}
          onChange={handleChangeSelect}
        >
          {sortedItems.map((item) => {
            return (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            );
          })}
        </Select>
        {error && (
          <FormHelperText variant="filled" error>
            <InputHelperText touched={error} error={error && error.message} />
          </FormHelperText>
        )}
      </FormControl>
      <FormControlLabel
        sx={commonStyles.checkbox}
        control={<Checkbox checked={isFiltered} color="primary" name="filtered" onChange={handleChangeCheckbox} />}
        label="指定した県産に登録のある銘柄に絞り込む"
      />
    </div>
  );
};
